/**
 * OrdersPage — cross-user order search at /orders (docs/features/admin-order-purchase-search).
 * Filter state lives in the URL (?search=&status=&page=) so views are shareable.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { cn } from '@/src/infrastructure/lib/utils';
import { getAdminOrderStats, listAdminOrders, type AdminOrderList, type AdminOrderStats } from '@/src/infrastructure/lib/api';

// Preset tabs → `status` query value ('' = all). Counts come from /stats.
const TABS: Array<{ label: string; status: string; count: (s: AdminOrderStats) => number }> = [
  { label: 'All', status: '', count: (s) => s.total },
  { label: 'Action required', status: 'CHECKOUT_CREATED,PAYMENT_PENDING,FAILED', count: (s) => s.actionRequired },
  { label: 'Completed', status: 'COMPLETED', count: (s) => s.completed },
  { label: 'Fulfillment', status: 'FULFILLMENT_IN_PROGRESS', count: (s) => s.fulfillmentInProgress },
  { label: 'Cancelled / expired', status: 'CANCELLED,EXPIRED', count: (s) => s.cancelled + s.expired },
];

export const ORDER_STATUS_TONE: Record<string, string> = {
  COMPLETED: 'border-emerald-500/40 text-emerald-600 bg-emerald-500/10',
  FAILED: 'border-rose-500/40 text-rose-600 bg-rose-500/10',
  PAYMENT_PENDING: 'border-amber-500/40 text-amber-600 bg-amber-500/10',
  CHECKOUT_CREATED: 'border-amber-500/40 text-amber-600 bg-amber-500/10',
};
export const orderTone = (s: string) => ORDER_STATUS_TONE[s] ?? 'border-slate-500/40 text-slate-500 bg-slate-500/10';
export const rupees = (paise: number) => `₹${(paise / 100).toFixed(2)}`;

export default function OrdersPage() {
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? '1') || 1;

  const [draft, setDraft] = useState(search);
  const [data, setData] = useState<AdminOrderList | null>(null);
  const [stats, setStats] = useState<AdminOrderStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const update = (next: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v ? p.set(k, v) : p.delete(k));
    setParams(p);
  };

  useEffect(() => setDraft(search), [search]); // browser back/forward

  useEffect(() => { getAdminOrderStats().then(setStats).catch(() => setStats(null)); }, []);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    setError(null);
    const q: Record<string, string> = { page: String(page), limit: '20' };
    if (search) q.search = search;
    if (status) q.status = status;
    listAdminOrders(q)
      .then((d) => !stale && setData(d))
      .catch((e) => !stale && setError(e instanceof Error ? e.message : 'Could not load orders'))
      .finally(() => !stale && setLoading(false));
    return () => { stale = true; };
  }, [search, status, page]);

  return (
    <div className="p-6 md:p-8 space-y-6">
      <header>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-on-surface">Orders &amp; purchases</h1>
        <p className="text-sm text-on-surface-variant">
          Search by order number, order/payment/invoice id, customer email, phone or name.
        </p>
      </header>

      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); update({ search: draft.trim(), page: '' }); }}
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-on-surface-variant" />
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={128}
            placeholder="ORD-2026-000142, order_…, pay_…, INV-…, email, phone"
            className="w-full rounded-full border border-border-subtle bg-surface pl-9 pr-9 py-2 text-sm"
          />
          {draft && (
            <button type="button" aria-label="Clear search" className="absolute right-3 top-2.5"
              onClick={() => { setDraft(''); update({ search: '', page: '' }); }}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.label}
            onClick={() => update({ status: t.status, page: '' })}
            className={cn('rounded-full border px-3 py-1.5 text-xs font-medium',
              status === t.status ? 'bg-primary text-on-primary border-primary' : 'border-border-subtle bg-surface')}
          >
            {t.label}{stats ? ` (${t.count(stats)})` : ''}
          </button>
        ))}
      </div>

      {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}

      <div className={cn('overflow-x-auto rounded-2xl border border-border-subtle transition-opacity', loading && 'opacity-50')} aria-busy={loading}>
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-on-surface-variant bg-surface-low/40">
            <tr>
              {['Order', 'Customer', 'Item', 'Amount', 'Status', 'Created'].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data?.items.map((o) => (
              <tr key={o.id} className="border-t border-border-subtle hover:bg-surface-low/30">
                <td className="px-4 py-3 font-mono"><Link className="underline" to={`/orders/${o.id}`}>{o.orderNumber}</Link></td>
                <td className="px-4 py-3">{o.user.name}<div className="text-xs text-on-surface-variant">{o.user.email}</div></td>
                <td className="px-4 py-3">{o.item.displayName}</td>
                <td className="px-4 py-3">{rupees(o.amountPaise)}</td>
                <td className="px-4 py-3">
                  <span className={cn('rounded-full border px-2 py-0.5 text-xs', orderTone(o.status))}>{o.status}</span>
                  {o.refund.hasRefund && <span className="ml-1 text-xs text-on-surface-variant">refunded</span>}
                </td>
                <td className="px-4 py-3">{new Date(o.createdAt).toLocaleString('en-IN')}</td>
              </tr>
            ))}
            {!loading && !data?.items.length && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-on-surface-variant">No orders match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <div className="flex items-center justify-between text-sm">
          <span>{data.pagination.total} orders</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => update({ page: String(page - 1) })} className="rounded-full border px-3 py-1 disabled:opacity-40">Prev</button>
            <span>Page {data.pagination.page} / {Math.max(data.pagination.totalPages, 1)}</span>
            <button disabled={!data.pagination.hasMore} onClick={() => update({ page: String(page + 1) })} className="rounded-full border px-3 py-1 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
